import { useField } from "@formiz/core";
import { UseFieldProps } from "@formiz/core/dist/types/field.types";
import { Button, ButtonVariant, Text, TextVariants } from "@next-platform/client-common-spicy-ui";
import { CHARACTER_VISIBILITY } from "@next-platform/client-common-core";
import { useCallback, useEffect } from "react";

interface ButtonProps {
  label: string;
  value: string | boolean;
  helperText: string;
}

interface VisibilityButtonsProps extends UseFieldProps {
  title: string;
  botVisibility: CHARACTER_VISIBILITY;
  setBotVisibility: (visibility: CHARACTER_VISIBILITY) => void;
  buttons: ButtonProps[];
  isDisabled: boolean;
}

function VisibilityButtons({ title, buttons = [], botVisibility, setBotVisibility, isDisabled, name, ...props }: VisibilityButtonsProps) {
  const { value, setValue, errorMessage } = useField({ ...props, name });
  const isPermanentlyPrivate = value === CHARACTER_VISIBILITY.PERMANENTLY_PRIVATE;

  // To update difinition visibility value based bot visibility
  useEffect(() => {
    if (title !== "Definition Visibility") return;
    setValue(![CHARACTER_VISIBILITY.PRIVATE, CHARACTER_VISIBILITY.HIDDEN].includes(botVisibility));
  }, [setValue, botVisibility, title]);

  const onChangeHandler = useCallback(
    (newValue: string | boolean) => {
      if (name === "visibility") {
        setBotVisibility(newValue as CHARACTER_VISIBILITY);
        setValue(newValue);
        return;
      }
      setValue(newValue === "true");
    },
    [setBotVisibility, setValue, name],
  );
  const { helperText } = buttons.find((option) => option.value === value) || {};
  return (
    <div className="flex flex-col gap-2">
      <Text variant={TextVariants.LABEL_LARGE_MEDIUM}>{title}</Text>
      <div className="flex flex-row gap-sm">
        {buttons.map((option) => (
          <Button
            key={String(option.value)}
            variant={
              isPermanentlyPrivate
                ? CHARACTER_VISIBILITY.PRIVATE === option.value
                  ? ButtonVariant.INVERSE
                  : ButtonVariant.SECONDARY
                : value === option.value
                  ? ButtonVariant.INVERSE
                  : ButtonVariant.SECONDARY
            }
            disabled={isDisabled}
            onClick={() => onChangeHandler(option.value)}
            aria-labelledby={String(option.value)}
          >
            {option.label}
          </Button>
        ))}
      </div>
      {helperText && (
        <Text variant={TextVariants.LABEL_SMALL_REGULAR} className="text-gray-11">
          {helperText}
        </Text>
      )}
      {errorMessage && <Text color="error">{errorMessage}</Text>}
    </div>
  );
}

export default VisibilityButtons;
